import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Paper, { type PaperProps } from "@mui/material/Paper";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import { ListItemIcon, InputAdornment, CircularProgress } from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import {
	createContext,
	useContext,
	forwardRef,
	useState,
	type FC,
	type ReactNode,
	type UIEvent,
	type CSSProperties,
} from "react";
import type { CustomSelectProps } from "./types/types";
import {
	menuPaperSx,
	menuZIndexSx,
	SELECT_DROPDOWN_LIST_MAX_HEIGHT,
	mergeSelectSx,
	menuItemSx,
	selectRenderValuePlaceholderSx,
	selectRenderValueSelectedSx,
	menuItemContentSx,
	menuItemLabelSx,
	listItemIconSx,
	checkIconSx,
	defaultSelectMenuProps,
} from "./styles/Select.styles";
import {
	formControlHeight,
	outlinedInputExternalLabelProps,
	selectOutlinedInputSx,
	formFieldLabelClassName,
} from "@config/theme/formControls";
import DropdownOptionInfoLink from "@components/common/DropdownOptionInfoLink/DropdownOptionInfoLink";
import { SelectFieldWrapper } from "./shared/SelectFieldWrapper";
import { FORM_PROPERTIES } from "../InputField";

const MenuPaperTopSlotContext = createContext<ReactNode>(null);

const SelectMenuPaper = forwardRef<HTMLDivElement, PaperProps>(function SelectMenuPaper(props, ref) {
	const topSlot = useContext(MenuPaperTopSlotContext);
	const { children, sx, ...rest } = props;
	return (
		<Paper
			ref={ref}
			{...rest}
			sx={[
				menuPaperSx,
				{ overflow: "hidden", p: 0, mt: 0.25 },
				...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
			]}>
			{topSlot != null ? (
				<Box
					role="presentation"
					onMouseDown={e => e.stopPropagation()}
					sx={{
						bgcolor: "background.paper",
						borderBottom: "1px solid",
						borderColor: "divider",
						px: 1.5,
						py: 1,
					}}>
					{topSlot}
				</Box>
			) : null}
			{children}
		</Paper>
	);
});

const CustomSelect: FC<CustomSelectProps> = ({
	options,
	selected,
	setSelected,
	label,
	labelClassName,
	required,
	placeholder,
	id,
	startIcon,
	onScrollEnd,
	menuHeader,
	menuFooter,
	onOpenChange,
	error,
	helperText,
	className,
	customWidth,
	controlHeight = formControlHeight,
	disabled = false,
	loading = false,
}) => {
	const useControlledMenu = menuHeader != null || menuFooter != null || onOpenChange != null;
	const [menuOpen, setMenuOpen] = useState(false);

	const widthSx = customWidth ? { width: customWidth, maxWidth: customWidth } : undefined;

	const handleMenuListScrollEnd = onScrollEnd
		? (e: UIEvent<HTMLElement>) => {
				const target = e.currentTarget;
				if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
					onScrollEnd();
				}
			}
		: undefined;

	const menuListProps = {
		...(onScrollEnd ? { onScroll: handleMenuListScrollEnd } : {}),
		sx: {
			maxHeight: SELECT_DROPDOWN_LIST_MAX_HEIGHT,
			overflowY: "auto" as const,
		},
	};

	const menuProps = menuHeader
		? {
				PaperProps: { component: SelectMenuPaper },
				MenuListProps: menuListProps,
				disableAutoFocusItem: true,
				sx: menuZIndexSx,
				TransitionProps: {
					onEntered: (node: HTMLElement) => {
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								const list = node.querySelector?.("ul[role='listbox']") as HTMLElement | null;
								if (list) list.scrollTop = 0;
							});
						});
					},
				},
			}
		: {
				...defaultSelectMenuProps,
				MenuListProps: menuListProps,
			};

	const wrapperClassName =
		[
			customWidth ? "inline-block" : undefined,
			disabled || loading ? "cursor-not-allowed [&_*]:!cursor-not-allowed" : undefined,
			className,
		]
			.filter(Boolean)
			.join(" ") || undefined;

	const selectControl = (
		<SelectFieldWrapper className={wrapperClassName} controlHeight={controlHeight}>
			<Select
				error={error}
				value={selected}
				disabled={disabled || loading}
				fullWidth={!customWidth}
				displayEmpty
				sx={mergeSelectSx(error, controlHeight, widthSx)}
				{...(useControlledMenu
					? {
							open: menuOpen,
							onOpen: () => {
								setMenuOpen(true);
								onOpenChange?.(true);
							},
							onClose: () => {
								setMenuOpen(false);
								onOpenChange?.(false);
							},
						}
					: {})}
				onChange={e => {
					const selectedOption = (Array.isArray(options) ? options : []).find(opt => opt.value === e.target.value);

					if (selectedOption?.disabled) {
						return;
					}

					if (e.target.value === "") {
						const emptyStringOption = (Array.isArray(options) ? options : []).find(opt => opt.value === "");
						if (emptyStringOption) {
							setSelected("");
							return;
						}
						return;
					}

					const value =
						selectedOption && typeof selectedOption.value === "number" ? selectedOption.value : e.target.value;
					setSelected(value);
				}}
				MenuProps={menuProps}
				input={
					<OutlinedInput
						{...outlinedInputExternalLabelProps}
						sx={selectOutlinedInputSx(error, controlHeight)}
						label={label}
						startAdornment={
							startIcon ? (
								<InputAdornment sx={{ marginRight: "-3px" }} position="start">
									{startIcon}
								</InputAdornment>
							) : undefined
						}
						endAdornment={
							loading ? (
								<InputAdornment position="end" sx={{ marginRight: "8px" }}>
									<CircularProgress size={20} color="inherit" />
								</InputAdornment>
							) : undefined
						}
					/>
				}
				renderValue={val => {
					if (loading) {
						return <span style={selectRenderValuePlaceholderSx as CSSProperties}>Loading...</span>;
					}

					const opt = (Array.isArray(options) ? options : []).find(
						o => String(o.value) === String(val) || o.value === val,
					);

					if (opt) {
						const isPlaceholderStyle = opt.isPlaceholder;
						const visibleLabel = opt.displayLabel || opt.label;
						return (
							<span
								title={!isPlaceholderStyle ? opt.label : undefined}
								style={
									(isPlaceholderStyle ? selectRenderValuePlaceholderSx : selectRenderValueSelectedSx) as CSSProperties
								}>
								{visibleLabel}
							</span>
						);
					}

					if (!val || val === "" || val === null || val === undefined) {
						const placeholderOption = (Array.isArray(options) ? options : []).find(opt => opt.isPlaceholder);
						return (
							<span style={selectRenderValuePlaceholderSx as CSSProperties}>
								{placeholderOption?.label || placeholder}
							</span>
						);
					}

					return <span style={selectRenderValueSelectedSx as CSSProperties}></span>;
				}}>
				{(Array.isArray(options) ? options : [])?.map(opt => {
					const isSelectablePlaceholder = opt.isPlaceholder && opt.value === "";
					const isSelected = !opt.isPlaceholder && String(selected) === String(opt.value);
					const isDisabledOption = !!opt.disabled;
					const isDisabledMenuItem = (!!opt.isPlaceholder && !isSelectablePlaceholder) || isDisabledOption;
					return (
						<MenuItem
							key={opt.value}
							value={opt.value}
							disabled={isDisabledMenuItem}
							sx={menuItemSx(!!opt.isPlaceholder && !isSelectablePlaceholder, isDisabledOption)}>
							<div style={menuItemContentSx as CSSProperties}>
								<span style={menuItemLabelSx as CSSProperties}>{opt.label}</span>
								{opt.infoHref ? <DropdownOptionInfoLink href={opt.infoHref} /> : null}
								{isSelected && (
									<ListItemIcon sx={listItemIconSx}>
										<CheckIcon fontSize="small" sx={checkIconSx} />
									</ListItemIcon>
								)}
							</div>
						</MenuItem>
					);
				})}
				{menuFooter}
			</Select>
		</SelectFieldWrapper>
	);

	return (
		<>
			{label && (
				<label className={labelClassName ?? formFieldLabelClassName} htmlFor={id}>
					{label}
					{required && (
						<>
							{" "}
							<span className="text-red-500">{FORM_PROPERTIES.REQUIRED_ASTERIK}</span>
						</>
					)}
				</label>
			)}
			{menuHeader ? (
				<MenuPaperTopSlotContext.Provider value={menuHeader}>{selectControl}</MenuPaperTopSlotContext.Provider>
			) : (
				selectControl
			)}
			{helperText && (
				<p
					className={`mt-1 ${error ? "" : "text-gray-500"}`}
					style={{
						fontSize: "11px",
						marginTop: "4px",
						...(error ? { color: "#FF6E4F" } : {}),
					}}>
					{helperText}
				</p>
			)}
		</>
	);
};

export default CustomSelect;
